// Action Item Email Helper — shared, pure recipient resolution + status types.
//
// SEMANTICS (per Joel's feedback on #113): an action-item email is a FOLLOW-UP
// from the owner (Joel / his assistant) TO the person who owns the item:
//   - item assigned to someone else → that assignee is the recipient
//     ("Hey Mani, you mentioned you'd… — what's the status?")
//   - item marked for the owner ("→ you") → the OWNER is the recipient — a
//     reminder Joel sends to himself (fine: it arrives from the assistant
//     identity reverra@agentmail.to, not his own Gmail).
// The composer To stays owner-editable; these helpers only pick the default.

export type EmailCandidate = { name: string | null; email: string };

export type ActionItemLike = {
  id?: string;
  text?: string | null;
  assignee_email: string | null;
  assignee_name: string | null;
  is_for_owner: boolean;
  status?: string;
};

export type ParticipantLike = {
  email: string | null;
  name: string | null;
  represents_owner: boolean;
};

// The lifecycle statuses of an action_item_emails row (migration 0045).
export type ActionItemEmailStatus =
  | 'draft_requested'
  | 'drafting'
  | 'draft_ready'
  | 'draft_error'
  | 'send_requested'
  | 'sending'
  | 'sent'
  | 'send_error';

export const TERMINAL_STATUSES: ReadonlySet<ActionItemEmailStatus> = new Set([
  'draft_ready',
  'draft_error',
  'sent',
  'send_error',
]);

// Case-insensitive + whitespace-normalized compare key. Deliberately EXACT
// beyond that: fuzzy name matching (first-name, contains) could resolve the
// WRONG person's email — for a real outbound send, a missing button beats a
// misdirected email. Items whose assignee can't be resolved simply aren't
// emailable from the UI.
const ci = (s: string | null | undefined): string =>
  (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Resolve the default recipient for ONE action item:
 *   - is_for_owner → the owner himself (self-reminder), needs ownerEmail;
 *   - assignee_email set → the assignee;
 *   - name-only assignee → look their email up among the meeting participants.
 * Returns [] when no address resolves (the item isn't emailable).
 */
export function resolveRecipients(
  item: ActionItemLike,
  participants: ParticipantLike[],
  ownerEmail: string | null,
): EmailCandidate[] {
  if (item.is_for_owner) {
    const owner = participants.find((p) => p.represents_owner && p.email);
    const email = owner?.email ?? ownerEmail;
    return email ? [{ email, name: owner?.name ?? null }] : [];
  }
  if (item.assignee_email && item.assignee_email.trim()) {
    return [{ email: item.assignee_email.trim(), name: item.assignee_name?.trim() || null }];
  }
  if (item.assignee_name && item.assignee_name.trim()) {
    const match = participants.find(
      (p) => !p.represents_owner && p.email && ci(p.name) === ci(item.assignee_name),
    );
    if (match?.email) return [{ email: match.email, name: match.name ?? null }];
  }
  return [];
}

export function isEmailable(
  item: ActionItemLike,
  participants: ParticipantLike[],
  ownerEmail: string | null,
): boolean {
  return resolveRecipients(item, participants, ownerEmail).length > 0;
}

/**
 * The OPEN action items belonging to one meeting participant — what their
 * follow-up email covers. The owner's row collects his is_for_owner items;
 * everyone else matches by assignee email (or name when the email is missing).
 */
export function itemsForParticipant<T extends ActionItemLike>(
  participant: ParticipantLike,
  items: T[],
): T[] {
  return items.filter((item) => {
    if ((item.status ?? 'open') !== 'open') return false;
    if (participant.represents_owner) return item.is_for_owner;
    if (item.is_for_owner) return false;
    if (item.assignee_email && participant.email) {
      return ci(item.assignee_email) === ci(participant.email);
    }
    return Boolean(
      item.assignee_name && participant.name && ci(item.assignee_name) === ci(participant.name),
    );
  });
}

/**
 * The OWNER's open items that MENTION this participant by name — e.g. the item
 * "Email Priscilla updated labels" is Joel's, but clicking Priscilla's row
 * should deliver it to her. First-name match requires ≥3 chars (avoids "Al"
 * matching "album"); matching is whitespace-normalized + case-insensitive.
 */
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function ownerItemsMentioning<T extends ActionItemLike>(
  participant: ParticipantLike,
  items: T[],
): T[] {
  if (participant.represents_owner) return [];
  const full = ci(participant.name);
  const firstRaw = (participant.name ?? '').trim().split(/\s+/)[0] ?? '';
  if (firstRaw.length < 3) return [];
  // Precision over recall (a missing button beats leaking an unrelated
  // commitment into someone's email):
  //   - full name: whole-word, case-insensitive ("priscilla carazo")
  //   - first name alone: whole-word, CAPITALIZED, and not at the very start
  //     of the text — so "Email Priscilla the labels" matches but "Will follow
  //     up with the vendor" / "Joel will send the pricing sheet" (auxiliary
  //     'will', 'don'⊂'done'-style substrings) never do.
  const fullRe = new RegExp(`\\b${escapeRe(full)}\\b`, 'i');
  const firstCapitalized = firstRaw[0]!.toUpperCase() + firstRaw.slice(1).toLowerCase();
  const firstRe = new RegExp(`\\b${escapeRe(firstCapitalized)}\\b`);
  return items.filter((item) => {
    if ((item.status ?? 'open') !== 'open' || !item.is_for_owner) return false;
    const raw = ((item as { text?: string | null }).text ?? '').trim();
    if (fullRe.test(ci(raw))) return true;
    const m = firstRe.exec(raw);
    return m !== null && m.index > 0;
  });
}

/** Split a free-typed To field into de-duped, plausibly-valid addresses. */
export function parseEmails(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const e = raw.trim();
    if (!e || !e.includes('@')) continue;
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}
